import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { RadioGroup, RadioGroupItem } from '@vaiu/ui/radio-group';
import { formatAmountForCurrency, isMmMoneyCurrency } from '@vaiu/utils/mmMoney';
import { useRedeemUx } from './RedeemUxContext';
import { FormattedCurrencyAmount, RedeemMmMoneyIssuerBanner } from './RedeemCurrencyDisplay';
import {
  leftoverAfterInvoicePay,
  verifyRevealedCashToken,
  type RedemptionApiResult,
  type RevealTokenResponse,
} from './redeemApiTypes';

function remainingDue(remaining: unknown, total: number): number {
  if (remaining === 0 || remaining === '0') return 0;
  if (remaining == null || remaining === '') return total;
  const n = typeof remaining === 'number' ? remaining : Number(remaining);
  if (!Number.isFinite(n) || Number.isNaN(n)) return total;
  return n;
}

function isInvoicePaySuccess(result: RedemptionApiResult | null | undefined): boolean {
  if (!result || typeof result !== 'object') return false;
  if (result.unableToPayInvoice === true) return false;
  if (result.error || result.status === 'error' || result.status === 'failed') return false;
  if (result.paidInvoice || result.details?.paidInvoice) return true;
  if (result.invoiceStatus || result.details?.invoiceStatus) return true;
  if (typeof result.remainingAmount === 'number') return true;
  if (typeof result.paymentAmount === 'number' && result.paymentAmount > 0) return true;
  return false;
}

function isCashTokenApplySuccess(result: RedemptionApiResult | null | undefined): boolean {
  if (!result || typeof result !== 'object') return false;
  if (result.unableToPayInvoice === true) return false;
  if (result.error || result.status === 'error' || result.status === 'failed') return false;
  return Boolean(
    result.paidInvoice ||
    result.details?.paidInvoice ||
    result.details?.invoicePayment ||
    result.transactionId ||
    (typeof result.amount === 'number' && result.amount > 0) ||
    (typeof result.details?.amount === 'number' && result.details.amount > 0)
  );
}

type RevealedCashToken = {
  key: string;
  amount: number;
  currency: string;
};

interface InvoicePaymentDecisionProps {
  invoiceData: {
    invoiceKey: string;
    invoiceAmount: number;
    invoiceCurrency: string;
    invoiceMessage?: string;
    invoiceCreator?: string;
    remainingAmount?: number;
    transactionFee?: number;
    totalDebited?: number;
    /** User prefers auto-pay: show summary first, then confirm full remaining or expand for partial */
    autoPayPreferred?: boolean;
  };
  isGuest?: boolean;
  onPaymentComplete: (result: any) => void;
  onPaymentError: (error: any) => void;
  onCancel: () => void;
}

const InvoicePaymentDecision: React.FC<InvoicePaymentDecisionProps> = ({
  invoiceData,
  isGuest = false, // Default to false
  onPaymentComplete,
  onPaymentError,
  onCancel
}) => {
  const [paymentAmount, setPaymentAmount] = useState(
    remainingDue(invoiceData.remainingAmount, invoiceData.invoiceAmount)
  );
  const [resolvedRemaining, setResolvedRemaining] = useState(
    remainingDue(invoiceData.remainingAmount, invoiceData.invoiceAmount)
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const payInFlightRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [cashTokenKey, setCashTokenKey] = useState('');
  const [revealedCashToken, setRevealedCashToken] = useState<RevealedCashToken | null>(null);
  const [paymentMode, setPaymentMode] = useState<'full' | 'partial'>('full');
  const paymentModeRef = useRef<'full' | 'partial'>('full');
  paymentModeRef.current = paymentMode;
  const [showAdvancedPayment, setShowAdvancedPayment] = useState(
    () => !invoiceData.autoPayPreferred
  );
  const { platformApi, toast, i18n, getUser, TopUpBalance, StripeProvider, emitTokenRedemptionBalanceUpdate } = useRedeemUx();
  const user = getUser();

  const fee = Math.max(0, Number(invoiceData.transactionFee) || 0);
  const remaining = resolvedRemaining;
  const totalDebited =
    typeof invoiceData.totalDebited === 'number'
      ? invoiceData.totalDebited
      : remaining + fee;

  useEffect(() => {
    setResolvedRemaining(remainingDue(invoiceData.remainingAmount, invoiceData.invoiceAmount));
  }, [invoiceData.remainingAmount, invoiceData.invoiceAmount]);

  useEffect(() => {
    let cancelled = false;
    const loadRemaining = async () => {
      if (!platformApi.getInvoiceDetails || !invoiceData.invoiceKey) return;
      try {
        const details = (await platformApi.getInvoiceDetails(invoiceData.invoiceKey)) as {
          remainder?: number | string;
          remainingAmount?: number | string;
          amount?: number | string;
        };
        if (cancelled || !details) return;
        const total = Number(details.amount) || invoiceData.invoiceAmount;
        const nextRemaining = remainingDue(
          details.remainder ?? details.remainingAmount,
          total
        );
        setResolvedRemaining(nextRemaining);
        if (paymentModeRef.current === 'full') {
          setPaymentAmount(nextRemaining);
        }
      } catch {
        /* keep remaining from props */
      }
    };
    void loadRemaining();
    return () => {
      cancelled = true;
    };
  }, [invoiceData.invoiceKey, invoiceData.invoiceAmount, platformApi]);

  const handlePaymentModeChange = useCallback((value: 'full' | 'partial') => {
    setPaymentMode(value);
    if (value === 'full') {
      setPaymentAmount(remaining);
    } else {
      setPaymentAmount(0);
    }
    setError(null);
  }, [remaining]);

  const handleAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setPaymentAmount(value);
    setError(null);
  }, []);

  const validatePayment = useCallback(() => {
    if (!paymentAmount || paymentAmount <= 0) {
      setError('Payment amount must be greater than 0');
      return false;
    }

    const maxDue = remaining;
    if (paymentAmount > maxDue) {
      const maxLabel = formatAmountForCurrency(maxDue, invoiceData.invoiceCurrency);
      setError(`Payment amount cannot exceed remaining amount of ${maxLabel}`);
      return false;
    }

    return true;
  }, [paymentAmount, remaining, invoiceData.invoiceCurrency]);

  // State for TopUpBalance component
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState(0);

  const releasePayLock = useCallback(() => {
    payInFlightRef.current = false;
    setIsProcessing(false);
  }, []);

  const handlePayInvoice = useCallback(async () => {
    if (payInFlightRef.current || isProcessing) {
      return;
    }
    if (!validatePayment()) {
      return;
    }

    console.log('🧾 Starting invoice payment process:', {
      paymentAmount,
      paymentMode,
      invoiceData,
      user: user ? { id: user._id, balances: user.balances } : null
    });

    payInFlightRef.current = true;
    setIsProcessing(true);
    setError(null);

    // For guests, we skip straight to the funding UI (TopUpBalance)
    if (isGuest) {
      console.log('💰 Guest payment detected, showing TopUpBalance for direct card payment');
      setTopUpAmount(paymentAmount);
      setShowTopUp(true);
      releasePayLock();
      return; // Skip the rest of the function for guests
    }

    const checkCurrency = invoiceData?.invoiceCurrency || 'USD';
    try {
      console.log('🧾 Paying invoice:', invoiceData.invoiceKey);
      const result = (await platformApi.payInvoice({
        invoiceCode: invoiceData.invoiceKey,
        paymentAmount,
        currency: checkCurrency,
      })) as RedemptionApiResult;

      console.log('✅ Invoice payment result:', result);

      if (!isInvoicePaySuccess(result)) {
        const failMessage =
          result?.unableToPayInvoice === true
            ? 'Invoice could not be paid from your balance. Add funds or try again.'
            : String(result?.message || result?.error || 'Invoice payment failed');
        throw new Error(failMessage);
      }

      const paidAmount =
        (result?.details?.amount as number | undefined) ||
        result.paymentAmount ||
        result?.amount ||
        paymentAmount;
      const remainder = leftoverAfterInvoicePay(result, resolvedRemaining, paymentAmount);
      const isPartialPayment = typeof remainder === 'number' && remainder > 0;
      const currency =
        (result?.details?.currency as string | undefined) ||
        result?.currency ||
        invoiceData?.invoiceCurrency ||
        'USD';
      const currentBalance = user?.balances?.[currency] ?? 0;
      if (paidAmount && currency) {
        emitTokenRedemptionBalanceUpdate(paidAmount, currency, currentBalance, invoiceData.invoiceKey);
      }

      toast({
        title: isPartialPayment
          ? i18n.__('redeemToken.partialPaymentSuccess')
          : i18n.__('redeemToken.paymentSuccess'),
        description: i18n.__('redeemToken.paymentSuccessDesc', {
          currency,
          amount: paidAmount,
          isPartial: isPartialPayment && remainder != null ? ` (${remainder} remaining)` : '',
        }),
      });

      if (onPaymentComplete) {
        onPaymentComplete({
          ...result,
          remainingAmount: remainder,
          invoiceRemainder: remainder,
          remainder,
          status: isPartialPayment ? 'partial' : 'completed',
        });
      }
    } catch (err: any) {
      console.error('❌ Payment process failed:', err);
      
      // Check for insufficient balance error (multiple possible formats)
      const errorMessage = err?.error?.message || err?.message || err?.toString() || '';
      const isInsufficientBalance = 
        errorMessage.toLowerCase().includes('insufficient') ||
        errorMessage.toLowerCase().includes('not enough') ||
        errorMessage.toLowerCase().includes('balance') ||
        errorMessage.toLowerCase().includes('could not be paid') ||
        err?.error?.name === 'BadRequestError' ||
        err?.error?.code === 'INSUFFICIENT_BALANCE' ||
        err?.code === 'INSUFFICIENT_BALANCE';
        
      if (isInsufficientBalance) {
        console.log('💰 Insufficient balance detected, showing TopUpBalance');
        console.log('Error details:', { err, errorMessage });
        
        // Store the amount needed for top-up
        setTopUpAmount(paymentAmount);
        setShowTopUp(true);
        releasePayLock();
        return;
      }
      
      // For other errors, show error message
      const finalErrorMessage = err?.error?.message || err?.message || 'Payment failed';
      setError(finalErrorMessage);
      toast({
        title: "Payment Failed",
        description: finalErrorMessage,
        variant: "destructive",
      });
      
      if (onPaymentError) {
        onPaymentError(err);
      }
      releasePayLock();
    }
  }, [
    paymentAmount,
    invoiceData,
    paymentMode,
    onPaymentComplete,
    onPaymentError,
    toast,
    isGuest,
    isProcessing,
    validatePayment,
    platformApi,
    user,
    emitTokenRedemptionBalanceUpdate,
    releasePayLock,
  ]);

  const handleReviewCashToken = useCallback(async () => {
    const tokenKey = cashTokenKey.trim().toUpperCase();
    if (payInFlightRef.current || isProcessing) return;
    if (!/^[A-Z0-9]{6}$/.test(tokenKey)) {
      setError(i18n.__('redeemToken.enterCompleteCode'));
      return;
    }
    if (tokenKey === invoiceData.invoiceKey.toUpperCase()) {
      setError(i18n.__('redeemToken.applyCashTokenSameCode'));
      return;
    }
    if (!user?.username && !user?._id) {
      setError(i18n.__('redeemToken.mustBeLoggedIn'));
      return;
    }

    payInFlightRef.current = true;
    setIsProcessing(true);
    setError(null);
    setRevealedCashToken(null);

    try {
      const revealed = (await platformApi.revealToken(tokenKey)) as RevealTokenResponse;
      const verified = verifyRevealedCashToken(revealed, invoiceData.invoiceCurrency);
      if (!verified.ok) {
        throw new Error(i18n.__(verified.errorKey, verified.params as Record<string, unknown> | undefined));
      }
      setRevealedCashToken({
        key: tokenKey,
        amount: verified.token.amount,
        currency: verified.token.currency,
      });
    } catch (err: any) {
      const message = err?.error?.message || err?.message || i18n.__('redeemToken.applyCashTokenFailed');
      setError(message);
    } finally {
      releasePayLock();
    }
  }, [
    cashTokenKey,
    i18n,
    invoiceData.invoiceCurrency,
    invoiceData.invoiceKey,
    isProcessing,
    platformApi,
    releasePayLock,
    user,
  ]);

  const handlePayWithCashToken = useCallback(async () => {
    const tokenKey = revealedCashToken?.key || cashTokenKey.trim().toUpperCase();
    if (payInFlightRef.current || isProcessing) return;
    if (!revealedCashToken || revealedCashToken.key !== tokenKey) {
      return handleReviewCashToken();
    }

    payInFlightRef.current = true;
    setIsProcessing(true);
    setError(null);

    try {
      const revealed = (await platformApi.revealToken(tokenKey)) as RevealTokenResponse;
      const verified = verifyRevealedCashToken(revealed, invoiceData.invoiceCurrency);
      if (!verified.ok) {
        setRevealedCashToken(null);
        throw new Error(i18n.__(verified.errorKey, verified.params as Record<string, unknown> | undefined));
      }
      setRevealedCashToken({
        key: tokenKey,
        amount: verified.token.amount,
        currency: verified.token.currency,
      });

      const result = (await platformApi.payInvoiceToken(tokenKey, {
        invoiceToken: invoiceData.invoiceKey,
      })) as RedemptionApiResult;

      if (!isCashTokenApplySuccess(result)) {
        throw new Error(String(result?.message || result?.error || i18n.__('redeemToken.applyCashTokenFailed')));
      }

      const currency =
        result.details?.currency ||
        result.currency ||
        revealedCashToken.currency ||
        invoiceData.invoiceCurrency;
      const paidAmount = result.details?.amount || result.amount || revealedCashToken.amount || 0;
      const remainder = leftoverAfterInvoicePay(result, resolvedRemaining, paidAmount);
      const isPartialPayment = typeof remainder === 'number' && remainder > 0;

      toast({
        title: isPartialPayment
          ? i18n.__('redeemToken.partialPaymentSuccess')
          : i18n.__('redeemToken.paymentSuccess'),
        description: i18n.__('redeemToken.paymentSuccessDesc', {
          currency,
          amount: paidAmount,
          isPartial: isPartialPayment && remainder != null ? ` (${remainder} remaining)` : '',
        }),
      });
      onPaymentComplete?.({
        ...result,
        remainingAmount: remainder,
        invoiceRemainder: remainder,
        remainder,
        status: isPartialPayment ? 'partial' : 'completed',
      });
    } catch (err: any) {
      const message = err?.error?.message || err?.message || i18n.__('redeemToken.applyCashTokenFailed');
      setError(message);
      onPaymentError?.(err);
    } finally {
      releasePayLock();
    }
  }, [
    cashTokenKey,
    handleReviewCashToken,
    i18n,
    invoiceData.invoiceCurrency,
    invoiceData.invoiceKey,
    isProcessing,
    onPaymentComplete,
    onPaymentError,
    platformApi,
    releasePayLock,
    revealedCashToken,
    toast,
  ]);

  // Handle successful top-up
  const handleTopUpSuccess = useCallback(async (newBalanceOrPaymentResult: any) => {
    console.log('💰 Top-up/guest payment successful, retrying invoice payment');
    setShowTopUp(false);
    
    // Card/top-up only funds the account. Always redeem the invoice afterward.
    void handlePayInvoice();
  }, [handlePayInvoice, isGuest, onPaymentComplete, toast]);

  // Handle top-up cancellation
  const handleTopUpCancel = useCallback(() => {
    console.log('❌ Top-up cancelled');
    setShowTopUp(false);
    setError('Payment cancelled.');
    releasePayLock();
  }, [releasePayLock]);

  const handleDoNothing = useCallback(() => {
    console.log('User chose not to pay invoice');
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  if (!invoiceData) {
    return null;
  }

  const isPartialPayment = paymentAmount < remaining;
  const remainingAfterPayment = remaining - paymentAmount;
  const processingLabel = i18n.__('redeemToken.processing') || 'Paying invoice…';
  const payBusy = isProcessing;

  const payButtonContent = (
    <>
      {payBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {payBusy
        ? processingLabel
        : isGuest
          ? 'Continue to Payment'
          : paymentMode === 'full'
            ? 'Pay Full Amount'
            : 'Pay Partial Amount'}
    </>
  );

  return (
    <div
      className="relative mt-6 p-6 border-2 border-orange-200 rounded-lg bg-orange-50"
      aria-busy={payBusy}
    >
      {showTopUp ? (
        <StripeProvider>
            <TopUpBalance
            currentBalance={isGuest ? 0 : user?.balances?.[invoiceData.invoiceCurrency] || 0}
            requiredAmount={topUpAmount}
            currency={invoiceData.invoiceCurrency}
            onSuccess={handleTopUpSuccess}
            onCancel={handleTopUpCancel}
            isOpen={true}
            isGuest={isGuest}
            context={{
                type: 'invoice-payment',
                returnPath: window.location.pathname,
                tokenAmount: paymentAmount,
                tokenCurrency: invoiceData.invoiceCurrency,
                tokenMessage: `Payment for invoice ${invoiceData.invoiceKey}`,
                invoiceKey: invoiceData.invoiceKey,
                invoiceAmount: paymentAmount,
            }}
            />
        </StripeProvider>
      ) : (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">
              Invoice Payment Decision
            </h3>
            <p className="text-sm text-muted-foreground">
              {i18n.__('redeemToken.invoiceRedemptionIntro')}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div>
            <h4 className="text-lg font-medium text-gray-800 mb-3">
              {i18n.__('redeemToken.tokenValueLabel')}
            </h4>
            <div className="p-4 border rounded-lg bg-white space-y-3">
              {isMmMoneyCurrency(invoiceData.invoiceCurrency) && (
                <RedeemMmMoneyIssuerBanner currencyKey={invoiceData.invoiceCurrency} />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">Invoice total</Label>
                  <div className="font-semibold text-lg">
                    <FormattedCurrencyAmount
                      amount={invoiceData.invoiceAmount}
                      currency={invoiceData.invoiceCurrency}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">
                    {i18n.__('redeemToken.amountDueLabel')}
                  </Label>
                  <div className="font-semibold text-lg text-red-600">
                    <FormattedCurrencyAmount amount={remaining} currency={invoiceData.invoiceCurrency} />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="text-sm text-red-600">
                  {i18n.__('redeemToken.processingFeeLabel')}
                </span>
                <span className="font-medium text-red-600">
                  <FormattedCurrencyAmount amount={fee} currency={invoiceData.invoiceCurrency} />
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-100 rounded-lg border border-orange-300">
                <span className="text-sm text-orange-900">
                  {i18n.__('redeemToken.totalDebitedLabel')}
                </span>
                <span className="font-semibold text-lg text-orange-900">
                  <FormattedCurrencyAmount amount={totalDebited} currency={invoiceData.invoiceCurrency} />
                </span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                <span className="text-muted-foreground">Invoice code</span>
                <span className="font-mono">{invoiceData.invoiceKey}</span>
              </div>
              {invoiceData.invoiceCreator && (
                <div className="text-sm text-gray-600">
                  <span className="text-muted-foreground">From: </span>
                  {invoiceData.invoiceCreator}
                </div>
              )}
              {invoiceData.invoiceMessage && (
                <div className="mt-1">
                  <Label className="text-sm text-gray-600">Message</Label>
                  <div className="text-gray-700 text-sm italic border rounded p-2 bg-blue-50/50">
                    {invoiceData.invoiceMessage}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border rounded-lg bg-white p-4">
            <Label htmlFor="invoice-cash-token" className="text-sm font-medium">
              {i18n.__('redeemToken.applyCashToken')}
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              {isGuest
                ? i18n.__('redeemToken.mustBeLoggedIn')
                : i18n.__('redeemToken.applyCashTokenHint')}
            </p>
            <div className="mt-2 flex gap-2">
              <Input
                id="invoice-cash-token"
                value={cashTokenKey}
                onChange={(event) => {
                  setCashTokenKey(
                    event.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 6)
                  );
                  setRevealedCashToken(null);
                }}
                placeholder={i18n.__('redeemToken.enterCode')}
                disabled={payBusy || isGuest}
                className="font-mono uppercase"
              />
              <Button
                type="button"
                variant="outline"
                onClick={revealedCashToken ? handlePayWithCashToken : handleReviewCashToken}
                disabled={payBusy || isGuest || cashTokenKey.length !== 6}
              >
                {revealedCashToken
                  ? i18n.__('redeemToken.applyCashTokenConfirm')
                  : i18n.__('redeemToken.applyCashToken')}
              </Button>
            </div>
            {revealedCashToken && (
              <div className="mt-3 space-y-1 text-sm">
                <p>
                  {i18n.__('redeemToken.applyCashTokenPreview', {
                    amount: formatAmountForCurrency(
                      revealedCashToken.amount,
                      revealedCashToken.currency
                    ),
                    currency: revealedCashToken.currency,
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {i18n.__('redeemToken.applyCashTokenLeftover')}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto p-0 text-xs"
                  onClick={() => setRevealedCashToken(null)}
                  disabled={payBusy}
                >
                  {i18n.__('redeemToken.applyCashTokenCancelPreview')}
                </Button>
              </div>
            )}
          </div>

          {invoiceData.autoPayPreferred && !showAdvancedPayment && (
            <div className="flex flex-col gap-2">
              <Button
                onClick={handlePayInvoice}
                disabled={payBusy || !paymentAmount || paymentAmount <= 0}
                aria-disabled={payBusy}
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                {payBusy ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    {processingLabel}
                  </>
                ) : (
                  i18n.__('redeemToken.confirmPayInvoice')
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdvancedPayment(true)}
                disabled={payBusy}
                className="w-full"
              >
                {i18n.__('redeemToken.payDifferentAmount')}
              </Button>
              <Button
                variant="outline"
                onClick={handleDoNothing}
                disabled={payBusy}
                className="w-full"
              >
                {i18n.__('redeemToken.doNothing')}
              </Button>
            </div>
          )}

          {(showAdvancedPayment || !invoiceData.autoPayPreferred) && (
            <>
              <div>
                <h4 className="text-lg font-medium text-gray-800 mb-3">Payment Options</h4>

                <RadioGroup
                  value={paymentMode}
                  onValueChange={handlePaymentModeChange}
                  className="space-y-3"
                  disabled={payBusy}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="full" id="full" disabled={payBusy} />
                    <Label htmlFor="full" className="cursor-pointer">
                      Pay full remaining amount (
                      <FormattedCurrencyAmount amount={remaining} currency={invoiceData.invoiceCurrency} />
                      )
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="partial" id="partial" disabled={payBusy} />
                    <Label htmlFor="partial" className="cursor-pointer">
                      Pay partial amount
                    </Label>
                  </div>
                </RadioGroup>

                {paymentMode === 'partial' && (
                  <div className="mt-3 ml-6">
                    <Label htmlFor="amount" className="text-sm font-medium">
                      Partial Amount:
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={remaining}
                      value={paymentAmount}
                      onChange={handleAmountChange}
                      placeholder="Enter amount"
                      disabled={payBusy}
                      className="mt-1 w-full max-w-xs"
                    />
                    {isPartialPayment && remainingAfterPayment > 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        Remaining after payment:{' '}
                        <FormattedCurrencyAmount
                          amount={remainingAfterPayment}
                          currency={invoiceData.invoiceCurrency}
                        />
                      </p>
                    )}
                  </div>
                )}
              </div>

              {!isGuest && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    You can change your auto-pay preference in your profile settings.
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handlePayInvoice}
                  disabled={payBusy || !paymentAmount || paymentAmount <= 0}
                  aria-disabled={payBusy}
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                >
                  {payButtonContent}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDoNothing}
                  disabled={payBusy}
                  className="flex-1"
                >
                  {i18n.__('redeemToken.doNothing')}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {payBusy && !showTopUp && (
        <div
          className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-orange-50/90"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-8 w-8 animate-spin text-orange-700" />
          <p className="mt-3 text-sm font-medium text-orange-900">{processingLabel}</p>
        </div>
      )}
    </div>
  );
};

export default InvoicePaymentDecision; 
